import { useRef, useState, useEffect } from "react";
import { AlertCircle } from "lucide-react";
import Input from "../common/Input";
import Button from "../common/Button";
import CustomSelect from "../common/CustomSelect";
import Textarea from "../common/TextArea";
import FileUpload from "../common/FileUpload";
import { toast } from "react-hot-toast";
import {
  createGallery,
  updateGalleryData,
} from "../../../services/galleryServices";
import { getGalleryById } from "../../../services/galleryServices";
import { useParams } from "react-router";
import { useNavigate } from "react-router";

const statusOptions = [
  { value: "active", label: "Active" },
  { value: "in-active", label: "Inactive" },
];

const viewOptions = [
  { value: "public", label: "Public" },
  { value: "private", label: "Private" },
];

const EditGalleryForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const imageRef = useRef();
  const [galleryName, setGalleryName] = useState("");
  const [galleryDescription, setGalleryDescription] = useState("");
  const [status, setStatus] = useState(null);
  const [viewType, setViewType] = useState(null);
  const [image, setImage] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isFetching, setIsFetching] = useState(true);

  useEffect(() => {
    const fetchGallery = async () => {
      if (!id) return;
      try {
        const data = await getGalleryById(id);
        setGalleryName(data.name || "");
        setGalleryDescription(data.description || "");
        setStatus(statusOptions.find((opt) => opt.value === data.status));
        setViewType(viewOptions.find((opt) => opt.value === data.view));
        setImage(data.images || []);
      } catch (err) {
        toast.error(err.message);
      } finally {
        setIsFetching(false);
      }
    };

    fetchGallery();
  }, [id]);

  const validate = () => {
    const errors = {};
    if (!galleryName.trim()) errors.galleryName = "Gallery Name is required";
    if (!galleryDescription.trim())
      errors.galleryDescription = "Description is required";
    if (!status) errors.status = "Status is required";
    if (!viewType) errors.viewType = "View type is required";
    if (!image.length) errors.image = "At least one image is required";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildPayload = () => ({
    name: galleryName.trim(),
    description: galleryDescription.trim(),
    status: status.value,
    view: viewType.value,
    images: image,
  });

  const handleSubmit = async () => {
    if (!validate()) {
      toast.error("Please fix form errors");
      return;
    }

    setFormErrors({});
    setIsLoading(true);

    try {
      const payload = buildPayload();
      await updateGalleryData({ galleryId: id, payload });

      toast.success("Gallery updated successfully");
      window.history.back();
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageDelete = async (updatedImages) => {
    const payload = buildPayload();
    payload.images = updatedImages;
    try {
      await updateGalleryData({ galleryId: id, payload });
    } catch (error) {
      toast.error(error.message || "Failed to delete image");
      throw error;
    }
  };

  if (isFetching) return <p>Loading gallery details...</p>;

  return (
    <div className="max-w-md p-6 bg-[#F5F0FF] rounded-lg space-y-4 font-sans">
      <Input
        label="Gallery Name"
        value={galleryName}
        onChange={(val) => {
          setGalleryName(val);
          if (formErrors.galleryName)
            setFormErrors((prev) => ({ ...prev, galleryName: "" }));
        }}
        required
        error={formErrors.galleryName}
      />

      <Textarea
        label="Gallery Description"
        value={galleryDescription}
        onChange={(e) => {
          setGalleryDescription(e.target.value);
          if (formErrors.galleryDescription)
            setFormErrors((prev) => ({ ...prev, galleryDescription: "" }));
        }}
        className="w-full border border-[#20202030] bg-white rounded-md px-3 py-2 text-sm focus:outline-none resize-none"
        error={formErrors.galleryDescription}
      />

      <CustomSelect
        label="Gallery Status"
        value={status}
        onChange={(val) => {
          setStatus(val);
          if (formErrors.status)
            setFormErrors((prev) => ({ ...prev, status: "" }));
        }}
        options={statusOptions}
        placeholder="Select status"
        error={formErrors.status}
      />

      <CustomSelect
        label="Gallery View"
        value={viewType}
        onChange={(val) => {
          setViewType(val);
          if (formErrors.viewType)
            setFormErrors((prev) => ({ ...prev, viewType: "" }));
        }}
        options={viewOptions}
        placeholder="Gallery view type"
        error={formErrors.viewType}
      />

      <div>
        <FileUpload
          ref={imageRef}
          mode="image"
          defaultLabel="Choose Images"
          multiple={true}
          onUpload={(res) => {
            if (res?.success && res.images?.length) {
              setImage((prev) => [
                ...prev,
                ...res.images.map((img) => img.imageUrl),
              ]);
              if (formErrors.image)
                setFormErrors((prev) => ({ ...prev, image: "" }));
            } else {
              toast.error("Image upload failed");
            }
          }}
          onDelete={handleImageDelete}
          setImage={setImage}
          image={image}
        />
        {formErrors.image && (
          <p className="text-red-600 font-semibold text-xs mt-1 flex items-center gap-1">
            <AlertCircle size={12} />
            {formErrors.image}
          </p>
        )}
      </div>

      <div className="pt-2 space-y-2">
        <Button onClick={handleSubmit} className="w-full" disabled={isLoading}>
          {isLoading ? "Processing..." : "Update Gallery"}
        </Button>
        <Button
          onClick={() => navigate("/admin/gallery/list")}
          variant="text"
          className="w-full"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default EditGalleryForm;
